import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import {
  Banner, bicOk, Button, ButtonLink, Card, Dialog, Field, formatIban, formatSortCode, ibanOk, Money, PageHeader, Pill, type LinkComponent,
} from '@elysian/ui';
import type { BankAccountSummary } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { shortDate } from '../invoices/shared';
import { CurrencyOptions, useYourCurrencies } from '../shared/CurrencyPicker';
import { BankFeeds } from './BankFeeds';

const RouterLink = Link as unknown as LinkComponent;

export function BankAccounts() {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const navigate = useNavigate();
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState<BankAccountSummary | null>(null);
  const [adding, setAdding] = useState(false);
  const mayEdit = can('settings.accounts');
  // Everyone sees the active accounts; whoever manages accounts also sees the inactive ones (#19).
  const all = useAsync(() => source.bankAccounts(mayEdit), [source, version, mayEdit], { keep: true });
  const data = all.state === 'ready' ? { ...all, data: all.data.filter((a) => a.is_active !== false) } : all;
  const inactive = all.state === 'ready' ? all.data.filter((a) => a.is_active === false) : [];
  const reload = () => setVersion((v) => v + 1);

  return (
    <>
      <PageHeader title="Bank accounts" subtitle="Balances in the books and on the latest statement, and the details printed on invoices"
        actions={mayEdit ? <Button variant="primary" onClick={() => setAdding(true)}>Add a bank account</Button> : undefined} />
      {data.state === 'error' && <Banner>Couldn’t load bank accounts: {data.error.message}</Banner>}
      {data.state === 'ready' && !data.data.some((a) => a.details) && (
        <Banner>No account has its IBAN entered, so invoices and statements don’t say where to pay. Add the details to the account customers pay into.</Banner>
      )}
      <Card>
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Bank accounts">
            <thead>
              <tr><th scope="col">Account</th><th scope="col">Printed on invoices</th><th scope="col" className="el-num">In the books</th><th scope="col" className="el-num">Statement</th><th scope="col">Status</th><th scope="col"><span className="el-vh">Actions</span></th></tr>
            </thead>
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={6}><div className="el-skeleton" style={{ height: 18 }} aria-label="Loading bank accounts" /></td></tr>}
              {data.state === 'ready' && data.data.map((a) => (
                <tr key={a.code} data-href={`/bank-accounts/${a.code}`}
                  onClick={(e) => (e.target as HTMLElement).closest('a, button') || navigate(`/bank-accounts/${a.code}`)}>
                  <td><Link className="el-row-link" to={`/bank-accounts/${a.code}`}><strong>{a.name}</strong></Link><div className="el-hint">{a.code} · {a.currency}</div></td>
                  <td>
                    {a.details ? (
                      <>
                        {a.details.iban && <div style={{ whiteSpace: 'nowrap' }}>{formatIban(a.details.iban)}</div>}
                        {a.details.sort_code && <div style={{ whiteSpace: 'nowrap' }}>{formatSortCode(a.details.sort_code)} · {a.details.account_number}</div>}
                        <div className="el-hint">{a.details.account_name}{a.details.bic ? ` · ${a.details.bic}` : ''}</div>
                        {a.details.is_default && <Pill tone="ok">Default</Pill>}
                      </>
                    ) : <span className="el-muted">Not set</span>}
                  </td>
                  <td className="el-num">
                    <Money value={a.ledger_balance} currency={a.currency} />
                    {a.currency !== 'EUR' && a.base_balance && <div className="el-hint">carried at <Money value={a.base_balance} /></div>}
                  </td>
                  <td className="el-num">
                    {a.statement_balance ? <Money value={a.statement_balance} currency={a.currency} /> : '—'}
                    {a.statement_date && <div className="el-hint">to {shortDate(a.statement_date)}</div>}
                  </td>
                  <td>
                    {a.unmatched_count > 0 ? <Pill tone="warn">{a.unmatched_count} to reconcile</Pill> : a.statement_balance ? <Pill tone="ok">Reconciled</Pill> : <Pill tone="draft">No statements</Pill>}
                    {a.last_signed_off && <div className="el-hint">Signed off {shortDate(a.last_signed_off)}</div>}
                  </td>
                  <td className="el-num">
                    <div className="el-row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                      {mayEdit && <EditButton account={a} onClick={() => setEditing(a)} />}
                      <ButtonLink as={RouterLink} to={`/bank?account=${a.code}`}>Reconcile</ButtonLink>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {mayEdit && data.state === 'ready' && (
        <p className="el-hint" style={{ margin: '8px 4px 0' }}>
          Use the pencil to change an account’s name, the details printed on invoices, or whether it’s active. An account with transactions stays active.
        </p>
      )}
      {inactive.length > 0 && (
        <div style={{ marginTop: 20 }}><Card>
          <div className="el-card-head" style={{ paddingTop: 16 }}><h2 className="el-h2">Inactive accounts</h2></div>
          <div className="el-table-wrap">
            <table className="el-table" aria-label="Inactive bank accounts">
              <thead><tr><th scope="col">Account</th><th scope="col"><span className="el-vh">Actions</span></th></tr></thead>
              <tbody>
                {inactive.map((a) => (
                  <tr key={a.code}>
                    <td><span className="el-muted">{a.name}</span><div className="el-hint">{a.code} · {a.currency}</div></td>
                    <td className="el-num">
                      <EditButton account={a} onClick={() => setEditing(a)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card></div>
      )}
      {data.state === 'ready' && <div style={{ marginTop: 20 }}><BankFeeds accounts={data.data} onSynced={reload} /></div>}
      {editing && (
        <EditBankAccount account={editing} onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); toast(msg); reload(); }} />
      )}
      {adding && (
        <AddBankAccount onClose={() => setAdding(false)}
          onAdded={(a) => { setAdding(false); toast(`${a.name} added as ${a.code}. Add the details printed on invoices if customers pay into it.`); reload(); setEditing({ ...a, is_active: true, in_use: false }); }} />
      )}
    </>
  );
}

/** Add a bank account (#18): a name and currency; the code follows the others unless you choose one. */
function AddBankAccount({ onClose, onAdded }: { onClose: () => void; onAdded: (a: BankAccountSummary) => void }) {
  const { source } = useSession();
  const { base, yours } = useYourCurrencies();
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState(base);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeError = code.trim() && !/^\d{3,6}$/.test(code.trim()) ? '3 to 6 digits' : null;

  async function add() {
    setBusy(true);
    setError(null);
    try {
      onAdded(await source.createBankAccount({ name: name.trim(), currency, ...(code.trim() ? { code: code.trim() } : {}) }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title="Add a bank account" onClose={onClose}
      actions={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !name.trim() || Boolean(codeError)} onClick={() => void add()}>Add account</Button>
      </>}>
      <Field id="nb-name" label="Name" hint="As you’d recognise it, for example “AIB current account” or “Revolut Business”">
        <input id="nb-name" className="el-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <div className="el-form-grid">
        <Field id="nb-currency" label="Currency" hint="Fixed once anything is posted to it">
          <select id="nb-currency" className="el-input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <CurrencyOptions base={base} yours={yours} />
          </select>
        </Field>
        <Field id="nb-code" label="Account code (optional)" hint="Left blank, the next free code after your other bank accounts" error={codeError}>
          <input id="nb-code" className="el-input" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Automatic" />
        </Field>
      </div>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}

/** The pencil on each row (#23): one place to change an account. */
function EditButton({ account, onClick }: { account: BankAccountSummary; onClick: () => void }) {
  return (
    <button type="button" className="el-icon-btn" data-tip="Edit" aria-label={`Edit ${account.name}`} onClick={onClick}>
      <Pencil size={16} aria-hidden="true" />
    </button>
  );
}

/**
 * Edit a bank account (#23): its name, the details printed on invoices and,
 * at the bottom, whether it's active. Accounts are active unless switched
 * off, and one with transactions can't be (the database checks this too).
 */
function EditBankAccount({ account, onClose, onSaved }: { account: BankAccountSummary; onClose: () => void; onSaved: (msg: string) => void }) {
  const { source, context } = useSession();
  const d = account.details;
  const wasActive = account.is_active !== false;
  const [title, setTitle] = useState(account.name);
  const [name, setName] = useState(d?.account_name ?? context?.organisation.legal_name ?? '');
  const [iban, setIban] = useState(d?.iban ? formatIban(d.iban) : '');
  const [bic, setBic] = useState(d?.bic ?? '');
  const [uk, setUk] = useState(Boolean(d?.sort_code) || context?.organisation.jurisdiction === 'UK');
  const [sort, setSort] = useState(d?.sort_code ? formatSortCode(d.sort_code) : '');
  const [accNo, setAccNo] = useState(d?.account_number ?? '');
  const [isDefault, setIsDefault] = useState(d?.is_default ?? false);
  const [active, setActive] = useState(wasActive);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = wasActive && account.in_use === true; // has transactions: stays active

  const sortDigits = sort.replace(/\D/g, '');
  const ibanError = iban.trim() && !ibanOk(iban) ? 'Those numbers don’t add up; check the IBAN against a statement' : null;
  const bicError = bic.trim() && !bicOk(bic) ? '8 or 11 letters and digits, for example AIBKIE2D' : null;
  const sortError = uk && sort.trim() && sortDigits.length !== 6 ? 'Six digits' : null;
  const accError = uk && accNo.trim() && !/^\d{8}$/.test(accNo.trim()) ? 'Eight digits' : null;
  const ukPartial = uk && Boolean(sortDigits) !== Boolean(accNo.trim()) ? 'Enter both the sort code and the account number' : null;
  const hasIban = Boolean(iban.trim());
  const hasUk = uk && Boolean(sortDigits && accNo.trim());
  const hasDetails = hasIban || hasUk;
  const detailsOk = !ibanError && !bicError && !sortError && !accError && !ukPartial && (!bic.trim() || hasIban) && (!hasDetails || Boolean(name.trim()));
  const ok = Boolean(title.trim()) && detailsOk;

  const nextDetails = hasDetails ? {
    account_name: name.trim(), iban: hasIban ? iban.replace(/\s/g, '') : null, bic: bic.trim() || null,
    sort_code: hasUk ? sortDigits : null, account_number: hasUk ? accNo.trim() : null,
  } : null;
  const detailsChanged = JSON.stringify(nextDetails) !== JSON.stringify(d ? {
    account_name: d.account_name, iban: d.iban ?? null, bic: d.bic ?? null, sort_code: d.sort_code ?? null, account_number: d.account_number ?? null,
  } : null) || (hasDetails && isDefault && !d?.is_default);
  const dirty = title.trim() !== account.name || detailsChanged || active !== wasActive;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const patch = { ...(title.trim() !== account.name ? { name: title.trim() } : {}), ...(active !== wasActive ? { is_active: active } : {}) };
      // Switching on comes first and off comes last, so the details are never saved to an inactive account.
      if (active && !wasActive) await source.updateAccount(account.code, patch);
      if (detailsChanged) await source.setBankDetails(account.code, nextDetails && { ...nextDetails, ...(isDefault ? { is_default: true } : {}) });
      if (!(active && !wasActive) && Object.keys(patch).length) await source.updateAccount(account.code, patch);
      onSaved(active !== wasActive
        ? active ? `${title.trim()} is active again` : `${title.trim()} is inactive: it no longer appears on invoices, bills or reconciliation`
        : detailsChanged && title.trim() === account.name ? (nextDetails ? `Bank details saved for ${account.name}` : `Bank details removed from ${account.name}`)
        : `${title.trim()} saved`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Edit ${account.name}`} onClose={onClose}
      actions={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !ok || !dirty} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>
      </>}>
      <Field id="bd-title" label="Account name" hint={`${account.code} · ${account.currency}. As you’d recognise it, for example “AIB current account”`}>
        <input id="bd-title" className="el-input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <h3 className="el-label" style={{ margin: '8px 0 0', fontSize: 14 }}>Printed on invoices</h3>
      <p className="el-hint" style={{ margin: 0 }}>So customers know where to pay; copy them from a bank statement. Leave blank for an account customers don’t pay into.</p>
      <Field id="bd-name" label="Account held in the name of" error={hasDetails && !name.trim() ? 'Needed with the bank details' : null}>
        <input id="bd-name" className="el-input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="el-form-grid">
        <Field id="bd-iban" label="IBAN" error={ibanError}>
          <input id="bd-iban" className="el-input" autoComplete="off" spellCheck={false} value={iban}
            onChange={(e) => setIban(e.target.value.toUpperCase())} onBlur={() => ibanOk(iban) && setIban(formatIban(iban))} placeholder="IE29 AIBK 9311 5212 3456 78" />
        </Field>
        <Field id="bd-bic" label="BIC" error={bicError ?? (bic.trim() && !hasIban ? 'A BIC goes with an IBAN' : null)}>
          <input id="bd-bic" className="el-input" autoComplete="off" spellCheck={false} value={bic} onChange={(e) => setBic(e.target.value.toUpperCase())} placeholder="AIBKIE2D" />
        </Field>
      </div>
      <label className="el-check"><input type="checkbox" checked={uk} onChange={(e) => setUk(e.target.checked)} /> UK account: also print the sort code and account number</label>
      {uk && (
        <div className="el-form-grid">
          <Field id="bd-sort" label="Sort code" error={sortError ?? (ukPartial && !sortDigits ? ukPartial : null)}>
            <input id="bd-sort" className="el-input" inputMode="numeric" value={sort} onChange={(e) => setSort(e.target.value)}
              onBlur={() => sortDigits.length === 6 && setSort(formatSortCode(sortDigits))} placeholder="20-00-00" />
          </Field>
          <Field id="bd-acc" label="Account number" error={accError ?? (ukPartial && !accNo.trim() ? ukPartial : null)}>
            <input id="bd-acc" className="el-input" inputMode="numeric" value={accNo} onChange={(e) => setAccNo(e.target.value)} placeholder="12345678" />
          </Field>
        </div>
      )}
      {hasDetails && !d?.is_default && (
        <label className="el-check"><input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Print these by default (an invoice design can still choose another account)</label>
      )}
      <div className="el-dialog-foot-option">
        <label className="el-check">
          <input type="checkbox" checked={active} disabled={locked} onChange={(e) => setActive(e.target.checked)} /> Active
        </label>
        <p className="el-hint" style={{ margin: '2px 0 0 26px' }}>
          {locked
            ? `It can’t be made inactive: ${account.in_use_reason ?? 'it has transactions in the books'}.`
            : active ? 'Untick to take it off invoices, bills and reconciliation. Its history is kept.' : 'Inactive: not offered on invoices, bills or reconciliation. Tick to use it again.'}
        </p>
      </div>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
